import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import StarIcon from '@material-ui/icons/Star';
import RoomServiceIcon from '@material-ui/icons/RoomService';
import FavoriteIcon from '@material-ui/icons/Favorite';
import { Link } from 'react-router-dom';
import PlaceCardV2 from '../components/common/PlaceCardV2';
import { useTravelGuide } from '../context/TravelGuideContext';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  banner: {
    borderRadius: 26,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: 'linear-gradient(118deg, #fff8ef 0%, #f3e7d9 60%, #ebdac9 130%)',
    boxShadow: '0 16px 32px rgba(63, 46, 31, 0.1)',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  heading: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#2f261f',
  },
  subheading: {
    color: '#645649',
    maxWidth: 620,
    marginTop: theme.spacing(0.8),
  },
  ctaRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  statCard: {
    borderRadius: 16,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: 'rgba(255, 252, 248, 0.86)',
    padding: theme.spacing(1.6),
  },
  statLabel: {
    color: '#7b6b5d',
    fontSize: '0.82rem',
    fontWeight: 700,
  },
  statValue: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    fontSize: '1.35rem',
    color: '#3b3027',
    marginTop: 4,
  },
  controls: {
    borderRadius: 18,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #fbf3e9 100%)',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  chipsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  empty: {
    borderRadius: 20,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #f7eee2 100%)',
    padding: theme.spacing(5, 3),
    textAlign: 'center',
  },
}));

const SavedPage = () => {
  const classes = useStyles();
  const { savedPlaceDetails } = useTravelGuide();
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating');

  const stats = useMemo(() => {
    const total = savedPlaceDetails.length;
    const avgRating = total
      ? (savedPlaceDetails.reduce((sum, place) => sum + (place.rating || 0), 0) / total).toFixed(1)
      : '0.0';
    const highRated = savedPlaceDetails.filter((place) => place.rating >= 4.5).length;
    return { total, avgRating, highRated };
  }, [savedPlaceDetails]);

  const filteredSaved = useMemo(() => {
    const q = query.trim().toLowerCase();
    let next = savedPlaceDetails.filter((place) => (
      q ? place.name.toLowerCase().includes(q) || (place.address || '').toLowerCase().includes(q) : true
    ));

    if (sortBy === 'rating') next = [...next].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'reviews') next = [...next].sort((a, b) => b.reviews - a.reviews);
    if (sortBy === 'name') next = [...next].sort((a, b) => a.name.localeCompare(b.name));

    return next;
  }, [savedPlaceDetails, query, sortBy]);

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Paper elevation={0} className={classes.banner}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" className={classes.heading}>Your saved collection</Typography>
            <Typography variant="body1" className={classes.subheading}>
              Keep your best discoveries in one place and promote top picks straight into your itinerary.
            </Typography>
            <Box className={classes.ctaRow}>
              <Button component={Link} to="/explore" variant="contained" color="primary">Discover More</Button>
              <Button component={Link} to="/itinerary" variant="outlined" color="primary">Build Itinerary</Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Paper elevation={0} className={classes.statCard}>
                  <Typography className={classes.statLabel}>Saved</Typography>
                  <Typography className={classes.statValue}>{stats.total}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper elevation={0} className={classes.statCard}>
                  <Typography className={classes.statLabel}>Avg Rating</Typography>
                  <Typography className={classes.statValue}>{stats.avgRating}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper elevation={0} className={classes.statCard}>
                  <Typography className={classes.statLabel}>4.5+ Picks</Typography>
                  <Typography className={classes.statValue}>{stats.highRated}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} className={classes.controls}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search saved places..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                <MenuItem value="rating">Sort by rating</MenuItem>
                <MenuItem value="reviews">Sort by reviews</MenuItem>
                <MenuItem value="name">Sort by name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box className={classes.chipsRow}>
              <Chip icon={<FavoriteIcon />} label={`${stats.total} saved`} />
              <Chip icon={<StarIcon />} label={`${stats.highRated} top rated`} />
              <Chip icon={<RoomServiceIcon />} label="Ready for itinerary" />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {savedPlaceDetails.length === 0 ? (
        <Paper elevation={0} className={classes.empty}>
          <Typography variant="h5">No saved places yet</Typography>
          <Typography color="textSecondary" style={{ marginTop: 8 }}>
            Save places from Explore or Place Details to build your personal shortlist.
          </Typography>
          <Box mt={2}>
            <Button component={Link} to="/explore" variant="contained" color="primary">Go to Explore</Button>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredSaved.map((place) => (
            <Grid item xs={12} md={6} lg={4} key={place.id}>
              <Box mb={1}>
                <PlaceCardV2 place={place} />
              </Box>
            </Grid>
          ))}
          {filteredSaved.length === 0 && (
            <Grid item xs={12}>
              <Paper elevation={0} className={classes.empty}>
                <Typography variant="h6">No matches for this search</Typography>
                <Typography color="textSecondary" style={{ marginTop: 8 }}>
                  Try another keyword or reset your search.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default SavedPage;
